import { INFRACTION_API_SEVERITY_LABEL } from "../constants";
import type {
	InfractionCatalogLookupApiResponse,
	InfractionIssuingAuthorityLookupApiResponse,
	InfractionVehicleLookupApiResponse,
} from "../types/infraction-api.types";

export type InfractionVehicleSelectItem = {
	id: string;
	value: number;
	plate: string;
	brand: string;
	model: string;
	label: string;
	displayTitle: string;
};

export type InfractionCatalogSelectItem = InfractionCatalogLookupApiResponse & {
	id: number;
	label: string;
	supportingText?: string;
};

export type InfractionIssuingAuthoritySelectItem =
	InfractionIssuingAuthorityLookupApiResponse & {
		id: number;
		label: string;
		codeLabel: string;
		supportingText?: string;
	};

export function mapInfractionVehicleLookupToSelectItem(
	vehicle: InfractionVehicleLookupApiResponse
): InfractionVehicleSelectItem {
	const brandModel = [vehicle.brand, vehicle.model].filter(Boolean).join(" ");
	const plate = vehicle.plate ?? "";
	const displayTitle = brandModel ? `${plate} - ${brandModel}` : plate;

	return {
		id: String(vehicle.id),
		value: vehicle.id,
		plate,
		brand: vehicle.brand ?? "",
		model: vehicle.model ?? "",
		label: displayTitle,
		displayTitle,
	};
}

export function buildCatalogSelectItemLabel(
	item: Pick<
		InfractionCatalogLookupApiResponse,
		"code" | "breakdown" | "description"
	>
): string {
	return [
		[item.code, item.breakdown].filter(Boolean).join("-"),
		item.description,
	]
		.filter(Boolean)
		.join(" - ");
}

export function buildCatalogSelectItemSupportingText(
	item: Pick<InfractionCatalogLookupApiResponse, "points" | "severity">
): string | undefined {
	const { points, severity } = item;

	if (!(points && severity)) {
		return;
	}

	const severityLabel = INFRACTION_API_SEVERITY_LABEL[severity];

	if (!severityLabel) {
		return;
	}

	return `${points} Pontos | ${severityLabel}`;
}

export function mapCatalogLookupToSelectItem(
	item: InfractionCatalogLookupApiResponse
): InfractionCatalogSelectItem {
	return {
		...item,
		label: buildCatalogSelectItemLabel(item),
		supportingText: buildCatalogSelectItemSupportingText(item),
	};
}

export function mapCatalogLookupToSelectItems(
	data: InfractionCatalogLookupApiResponse[]
): InfractionCatalogSelectItem[] {
	return data.map(mapCatalogLookupToSelectItem);
}

export function syntheticCatalogItemId(
	code: string,
	breakdown: string | null | undefined
): number {
	const key = `${code}|${breakdown ?? "0"}`;
	let hash = 0;

	for (let index = 0; index < key.length; index += 1) {
		hash = hash * 31 + key.charCodeAt(index);
	}

	return -Math.abs(hash || 1);
}

export function buildCatalogSelectItemFromDetail(detail: {
	infractionCode: string | null;
	breakdown: string | null;
	description: string | null;
	severity: number | null | undefined;
	points: number | null;
	offenderType: number | null | undefined;
}): InfractionCatalogSelectItem | null {
	if (!detail.infractionCode) {
		return null;
	}

	const code = detail.infractionCode;
	const breakdown = detail.breakdown;

	return {
		id: syntheticCatalogItemId(code, breakdown),
		code,
		breakdown,
		description: detail.description,
		severity: detail.severity ?? null,
		points: detail.points,
		offenderType: detail.offenderType ?? 0,
		baseAmount: null,
		label: buildCatalogSelectItemLabel({
			code,
			breakdown,
			description: detail.description,
		}),
		supportingText: buildCatalogSelectItemSupportingText({
			points: detail.points,
			severity: detail.severity ?? null,
		}),
	};
}

export function mergeCatalogSelectItems(
	lookupItems: InfractionCatalogSelectItem[],
	selectedItem: InfractionCatalogSelectItem | null
): InfractionCatalogSelectItem[] {
	if (!selectedItem) {
		return lookupItems;
	}

	const alreadyListed = lookupItems.some(
		(item) =>
			item.id === selectedItem.id ||
			(item.code === selectedItem.code &&
				item.breakdown === selectedItem.breakdown)
	);

	if (alreadyListed) {
		return lookupItems;
	}

	return [selectedItem, ...lookupItems];
}

export function mergeIssuingAuthorityLookupItems(
	lookupItems: InfractionIssuingAuthorityLookupApiResponse[],
	selectedItem: InfractionIssuingAuthorityLookupApiResponse | null
): InfractionIssuingAuthorityLookupApiResponse[] {
	if (!selectedItem) {
		return lookupItems;
	}

	const alreadyListed = lookupItems.some((item) => item.id === selectedItem.id);

	if (alreadyListed) {
		return lookupItems;
	}

	return [selectedItem, ...lookupItems];
}

export function buildIssuingAuthorityLookupFromDetail(detail: {
	issuingAuthorityId: number;
	issuingAuthorityCode: string | null;
	issuingAuthorityDescription: string | null;
	issuingAuthorityIsAntt?: boolean;
	isAntt?: boolean;
}): InfractionIssuingAuthorityLookupApiResponse | null {
	if (!detail.issuingAuthorityId) {
		return null;
	}

	return {
		id: detail.issuingAuthorityId,
		code: detail.issuingAuthorityCode,
		description: detail.issuingAuthorityDescription,
		isAntt: detail.issuingAuthorityIsAntt ?? detail.isAntt ?? false,
	};
}

export function mapIssuingAuthorityToCodeSelectItems(
	data: InfractionIssuingAuthorityLookupApiResponse[]
): InfractionIssuingAuthoritySelectItem[] {
	return data.map((authority) => ({
		...authority,
		id: authority.id,
		label: authority.code ?? "",
		codeLabel: authority.code ?? "",
		supportingText: authority.description ?? undefined,
	}));
}

export function mapIssuingAuthorityToNameSelectItems(
	data: InfractionIssuingAuthorityLookupApiResponse[]
): InfractionIssuingAuthoritySelectItem[] {
	return data.map((authority) => ({
		...authority,
		id: authority.id,
		label: authority.description ?? authority.code ?? "Órgão emissor",
		codeLabel: authority.code ?? "",
		supportingText: authority.code ?? undefined,
	}));
}

export function normalizePlateForSearch(value: string): string {
	return value.replace(/[^a-z0-9]/gi, "").toUpperCase();
}

export function resolveOptionalDateField(
	value?: string | null
): string | undefined {
	const trimmed = value?.trim();

	if (!trimmed) {
		return;
	}

	return trimmed;
}

export function buildOccurredAtIso(date: string, time: string): string {
	const [year, month, day] = date.split("-").map(Number);
	const [hour, minute] = time.split(":").map(Number);

	const occurredAt = new Date(
		year,
		month - 1,
		day,
		hour ?? 0,
		minute ?? 0,
		0,
		0
	);

	return occurredAt.toISOString();
}

export function formatCurrencyBR(value: string): string {
	const numbersOnly = value.replace(/\D/g, "");

	if (numbersOnly.length === 0) {
		return "";
	}

	const number = Number(numbersOnly) / 100;

	return number.toLocaleString("pt-BR", {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	});
}

export function parseDisplayBRToNumber(value: string): number | undefined {
	const numbersOnly = value.replace(/\D/g, "");
	if (numbersOnly.length === 0) {
		return;
	}
	const num = Number(numbersOnly) / 100;
	return Number.isNaN(num) ? undefined : num;
}

export function formatNumberToDisplayBR(value: number): string {
	return value.toLocaleString("pt-BR", {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	});
}
